import { Box, Checkbox, TableCell } from "@mui/material";
import {
    Color,
    DotLegalButton,
    DotLegalChip,
    DotLegalLink,
    DotLegalOverflowMenu,
    DotLegalTable,
    ITableHeader,
    ServerSideConfig,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import TableRow from "@common/components/tableRow/TableRow";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import FilterBar from "@common/components/filterBar/FilterBar";
import SavedViewsMenu from "@common/components/savedViews/SavedViewsMenu";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import { FilterPage } from "@common/pageView/PageView.types";
import { RiskFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import { SortDirection } from "@common/api/filterTypes";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { useEntityAssessmentsTable } from "./EntityAssessmentsTable.hooks";
import { useEntityAssessmentsTableStyles } from "./EntityAssessmentsTable.styles";
import { RiskMitigationSummary, RiskOperationalColumn, RiskOperationalItem } from "../RiskOperationalOverview.types";
import { formatExposureCost, formatExposureValue } from "../../riskExposure/formatExposureValue";
import { RiskExposureMode } from "../../riskExposure/RiskExposure.types";
import RiskExposureBanner from "../../riskExposure/RiskExposureBanner";
import RiskCountCellLink from "../../riskControls/RiskCountCellLink";
import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import LinkedControlStatus from "../../riskControls/LinkedControlStatus";
import LinkedControlsDialog from "../../riskControls/LinkedControlsDialog";
import IndicatorRiskChip from "../../IndicatorRiskChip";
import { RiskTreatmentType, treatmentChipColor } from "../../riskTreatmentType";
import RiskEntityGraphDialog from "../../riskEntityGraph/RiskEntityGraphDialog";
import BulkAssessDialog from "../bulkAssessDialog/BulkAssessDialog";
import { BulkAssessSelection } from "../useBulkAssessSelection";
import BulkMitigateDialog from "../bulkMitigateDialog/BulkMitigateDialog";
import { BulkMitigateSelection } from "../useBulkMitigateSelection";
import AddEntitiesDialog from "../addEntitiesDialog/AddEntitiesDialog";
import RemoveEntityDialog from "../removeEntityDialog/RemoveEntityDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface IEntityAssessmentsTable {
    riskAreaId: string;
    // Owned by the host so the bulk buttons can sit in the host's own toolbar; the checkbox column, select-all
    // and the dialogs live here.
    bulkAssess: BulkAssessSelection;
    bulkMitigate: BulkMitigateSelection;
}

// The per-entity risk table of one risk area: exposure banner, toolbar, server-side table, its own Add entities
// button and the dialogs its rows and actions open. Fetches its own data, so a host only passes the area id.
// Remount it (key) when the area changes, since the saved view state is read once on mount.
function EntityAssessmentsTable({ riskAreaId, bulkAssess, bulkMitigate }: IEntityAssessmentsTable) {
    const styles = useEntityAssessmentsTableStyles();
    const { translateString, translateNumber } = useTranslation();
    const { addonsFeatures } = usePlanContext();

    const {
        result,
        isLoading,
        pageNumber,
        pageSize,
        sortByColumn,
        sortDirection,
        onSortChange,
        onPageChange,
        filterOptions,
        onFilterChange,
        clearFilterData,
        searchText,
        onSearchTextChange,
        visibleColumns,
        renderedColumns,
        tableFields,
        hasDisplayChanged,
        hasFilterChanged,
        isSavedView,
        savedView,
        currentViewId,
        filterData,
        onColumnsSave,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getTypeLabel,
        getEntityStatusLabel,
        getTagChips,
        getBusinessAreaChips,
        getResponsibleName,
        riskLevelIndicators,
        isOverAppetite,
        getMitigationDisplay,
        navigateToAssessments,
        refetch,
        getItemUrl,
        entityToRemove,
        openRemoveEntity,
        closeRemoveEntity,
        isSearch,
        entityGraphTarget,
        openEntityGraph,
        closeEntityGraph,
        controlsDialogItem,
        openControlsDialog,
        closeControlsDialog,
        isAddEntitiesOpen,
        openAddEntities,
        closeAddEntities,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = useEntityAssessmentsTable(riskAreaId);

    // The table renders a single checkbox column, so at most one selection mode is active at a time; drive the
    // table's selection wiring off whichever one it is.
    const activeSelection = bulkAssess.isSelectionMode ? bulkAssess : bulkMitigate.isSelectionMode ? bulkMitigate : null;
    const isSelectionMode = activeSelection !== null;

    const pageItems = result?.items ?? [];

    const onBulkAssessSaved = () => {
        bulkAssess.exitSelectionMode();
        bulkAssess.closeBulkAssessDialog();
        refetch();
    };

    const onBulkMitigateSaved = () => {
        bulkMitigate.exitSelectionMode();
        bulkMitigate.closeBulkMitigateDialog();
        refetch();
    };

    // The triangle asserts something about the row entity, so the Linked risk column has to say whose level is
    // over appetite: there the level is the neighbour's, and the row itself may sit comfortably within appetite.
    const renderRiskChip = (level: number, label: string, overAppetiteTranslationKey = "riskGovernanceOverAppetite") => (
        <Box sx={styles.riskCell}>
            <IndicatorRiskChip
                level={level}
                label={label}
                riskLevelIndicators={riskLevelIndicators}
                overAppetiteWarningText={isOverAppetite(level) ? translateString(overAppetiteTranslationKey) : undefined}
            />
        </Box>
    );

    const renderMitigation = (mitigation: RiskMitigationSummary | null) => {
        const display = getMitigationDisplay(mitigation);
        if (display.kind === "none") return null;
        const labelKey = display.count === 1 ? "mitigationCountSingle" : "mitigationCountPlural";
        return <DotLegalChip value={translateString(labelKey, { number: display.count })} color={Color.graniteGray} size="small" isSelected />;
    };

    const renderTreatmentCount = (count: number, treatmentType: RiskTreatmentType, countLabelKeyBase: string) => {
        if (count <= 0) return null;
        const labelKey = `${countLabelKeyBase}${count === 1 ? "Single" : "Plural"}`;
        return <DotLegalChip value={translateString(labelKey, { number: count })} color={treatmentChipColor[treatmentType]} size="small" isSelected />;
    };

    const renderRiskAcceptances = (count: number) => renderTreatmentCount(count, RiskTreatmentType.Accept, "riskAcceptanceCount");

    const renderAvoidances = (count: number) => renderTreatmentCount(count, RiskTreatmentType.Avoid, "riskAvoidanceCount");

    const columnHeader: Record<RiskOperationalColumn, string> = {
        [RiskOperationalColumn.Type]: translateString("type"),
        [RiskOperationalColumn.Name]: translateString("entity"),
        [RiskOperationalColumn.EntityStatus]: translateString("entityStatus"),
        [RiskOperationalColumn.Tags]: translateString("tags"),
        [RiskOperationalColumn.BusinessArea]: translateString("businessArea"),
        [RiskOperationalColumn.Responsible]: translateString("responsible"),
        [RiskOperationalColumn.Risk]: translateString("risk"),
        [RiskOperationalColumn.AfterMitigation]: translateString("afterMitigation"),
        [RiskOperationalColumn.LinkedRisk]: translateString("linkedRisk"),
        [RiskOperationalColumn.Mitigation]: translateString("mitigations"),
        [RiskOperationalColumn.RiskAcceptance]: translateString("acceptances"),
        [RiskOperationalColumn.Avoidance]: translateString("avoidances"),
        [RiskOperationalColumn.ControlCount]: translateString("controlCount"),
        [RiskOperationalColumn.ControlStatus]: translateString("controlStatus"),
        [RiskOperationalColumn.EstimatedLoss]: translateString("riskExposure"),
        [RiskOperationalColumn.EstimatedCost]: translateString("estimatedCost"),
        [RiskOperationalColumn.ExposureAfterMitigation]: translateString("exposureAfterMitigationLabel"),
    };

    const headers: Array<ITableHeader<RiskOperationalItem>> = renderedColumns.map((column) => ({
        text: columnHeader[column],
        property: column,
        // Control status is a task-derived rollup that is not sortable in SQL; the count column stays sortable.
        notSortable: column === RiskOperationalColumn.ControlStatus,
    }));

    const formatModeExposure = (value: number | null) =>
        formatExposureValue(
            value,
            result?.exposureMode ?? RiskExposureMode.None,
            result?.exposureCurrencyCode ?? null,
            result?.exposureScaleUpperBound ?? null,
            translateNumber
        );
    const formatCost = (value: number | null) => formatExposureCost(value, result?.exposureCurrencyCode ?? null, translateNumber);

    const totalCount = result?.totalCount ?? 0;

    const serverSideConfig: ServerSideConfig = {
        order: sortDirection === SortDirection.DESC ? "desc" : "asc",
        orderBy: sortByColumn,
        onSortChange: (columnId, direction) => {
            onSortChange(columnId as RiskOperationalColumn, direction === "asc" ? SortDirection.ASC : SortDirection.DESC);
        },
        page: pageNumber,
        from: totalCount > 0 ? (pageNumber - 1) * pageSize + 1 : 0,
        to: Math.min(pageNumber * pageSize, totalCount),
        totalPages: result?.totalPages ?? 1,
        totalCount: totalCount,
        onPageChange: onPageChange,
    };

    const renderCell = (column: RiskOperationalColumn, item: RiskOperationalItem) => {
        switch (column) {
            case RiskOperationalColumn.Type:
                return (
                    <TableCell key={column}>
                        <EntityTypeIconWithName type={item.type} name={getTypeLabel(item.type)} />
                    </TableCell>
                );
            case RiskOperationalColumn.Name:
                return (
                    <TableCell key={column}>
                        <DotLegalLink to={getItemUrl(item)} linkColor="primary" bold stopPropagation>
                            {item.name}
                        </DotLegalLink>
                    </TableCell>
                );
            case RiskOperationalColumn.EntityStatus:
                return <TableCell key={column}>{getEntityStatusLabel(item.entityStatus)}</TableCell>;
            case RiskOperationalColumn.Tags:
                return (
                    <TableCell key={column}>
                        <ColoredChipsCell items={getTagChips(item.tagIds)} />
                    </TableCell>
                );
            case RiskOperationalColumn.BusinessArea:
                return (
                    <TableCell key={column}>
                        <ColoredChipsCell items={getBusinessAreaChips(item.businessAreaIds)} />
                    </TableCell>
                );
            case RiskOperationalColumn.Responsible:
                return <TableCell key={column}>{getResponsibleName(item.responsibleId)}</TableCell>;
            case RiskOperationalColumn.Risk:
                return <TableCell key={column}>{item.highestRisk && renderRiskChip(item.highestRisk.risk, item.highestRisk.riskLabel)}</TableCell>;
            case RiskOperationalColumn.AfterMitigation:
                return <TableCell key={column}>{item.afterMitigation && renderRiskChip(item.afterMitigation.risk, item.afterMitigation.riskLabel)}</TableCell>;
            case RiskOperationalColumn.LinkedRisk:
                // Clicking the chip opens the entity graph rooted at the row entity: the graph IS the number's
                // provenance (which linked entity carries the level), so they can never disagree.
                return (
                    <TableCell key={column}>
                        {item.linkedRisk && (
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openEntityGraph(item);
                                }}
                                sx={{ cursor: "pointer", display: "inline-flex" }}
                            >
                                {renderRiskChip(item.linkedRisk.risk, item.linkedRisk.riskLabel, "linkedRiskOverAppetite")}
                            </Box>
                        )}
                    </TableCell>
                );
            case RiskOperationalColumn.Mitigation:
                return <TableCell key={column}>{renderMitigation(item.mitigation)}</TableCell>;
            case RiskOperationalColumn.RiskAcceptance:
                return <TableCell key={column}>{renderRiskAcceptances(item.riskAcceptanceCount)}</TableCell>;
            case RiskOperationalColumn.Avoidance:
                return <TableCell key={column}>{renderAvoidances(item.avoidanceCount)}</TableCell>;
            case RiskOperationalColumn.ControlCount:
                return (
                    <TableCell key={column} align="right">
                        <RiskCountCellLink count={item.linkedControlIds.length} onClick={() => openControlsDialog(item)} />
                    </TableCell>
                );
            case RiskOperationalColumn.ControlStatus:
                return (
                    <TableCell key={column}>
                        <LinkedControlStatus status={item.controlStatus} />
                    </TableCell>
                );
            case RiskOperationalColumn.EstimatedLoss:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(item.estimatedLoss)}
                    </TableCell>
                );
            case RiskOperationalColumn.EstimatedCost:
                return (
                    <TableCell key={column} align="right">
                        {formatCost(item.estimatedCost)}
                    </TableCell>
                );
            case RiskOperationalColumn.ExposureAfterMitigation:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(item.exposureAfterMitigation)}
                    </TableCell>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <TablePagePaper>
                <RiskExposureBanner
                    exposure={result?.exposure ?? null}
                    exposureMode={result?.exposureMode ?? RiskExposureMode.None}
                    exposureCurrencyCode={result?.exposureCurrencyCode ?? null}
                    exposureScaleUpperBound={result?.exposureScaleUpperBound ?? null}
                />

                <ListPageToolbar
                    savedViewsMenu={
                        <SavedViewsMenu
                            currentViewId={currentViewId}
                            parentIsLoading={isLoading}
                            currentFilterData={filterData}
                            currentDisplayData={visibleColumns}
                            onViewChange={onChangeView}
                            onViewSaved={onViewSaved}
                            onViewDeleted={onViewDeleted}
                            filterPage={FilterPage.RiskOperationalOverview}
                            scopeId={riskAreaId}
                            hasFilterOrDisplayDataChanged={hasFilterChanged || hasDisplayChanged}
                        />
                    }
                    leftContent={
                        <FilterBar
                            isLoading={isLoading}
                            fetchFilterData
                            filterData={filterOptions}
                            onMenuClose={onFilterChange}
                            onRemove={onFilterChange}
                            pinnedFilterIds={pinnedFilterIds}
                            onPinnedFilterIdsChange={setPinnedFilterIds}
                            onClearFilter={hasFilterChanged ? () => clearFilterData() : undefined}
                            clearFilterText={hasFilterChanged ? (isSavedView ? "savedView" : "defaultView") : undefined}
                            searchValue={searchText}
                            onSearchChange={onSearchTextChange}
                            searchLabel={translateString("search")}
                        />
                    }
                    rightContent={
                        <>
                            {savedView && (
                                <TableDisplayFields
                                    selectedFields={visibleColumns}
                                    onSave={onColumnsSave}
                                    options={tableFields}
                                    hasDisplayChanged={hasDisplayChanged}
                                    isCustomView={isSavedView}
                                    savedViewFields={savedView.displayData}
                                    groupingOrder={RiskFieldGroupingOrder}
                                />
                            )}
                            {savedView && (
                                <ExcelExportButton
                                    onClick={onDownloadExcelReportClick}
                                    isDisabled={isLoading}
                                    isPlanLocked={!addonsFeatures.customLists}
                                    planLockedText={translateString("upgradeToCustomList")}
                                />
                            )}
                        </>
                    }
                />

                <DotLegalTable
                    internalScroll
                    hasOverflowMenu={!isSelectionMode}
                    headers={headers}
                    defaultOrderBy={RiskOperationalColumn.Name}
                    defaultOrder={"asc"}
                    data={pageItems}
                    isLoading={isLoading}
                    disableFilterAndSortAndSlicing
                    serverSide={serverSideConfig}
                    rowsPerPage={pageSize}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={isSearch ? translateString("noDataMatchingCriteria") : translateString("noEntitiesInRiskArea")}
                    clickableRows
                    onSelectAllChanged={activeSelection ? () => activeSelection.onSelectAllChanged(pageItems) : undefined}
                    headerCheckBoxState={activeSelection ? activeSelection.getHeaderCheckBoxState(pageItems) : undefined}
                    renderRow={(item) => (
                        <TableRow
                            key={`${item.type}:${item.entityId}`}
                            onClick={() => (activeSelection ? activeSelection.toggleEntity(item) : navigateToAssessments(item))}
                            sx={{ cursor: "pointer" }}
                        >
                            {activeSelection && (
                                <TableCell padding="checkbox">
                                    <Checkbox checked={activeSelection.isSelected(item)} readOnly tabIndex={-1} />
                                </TableCell>
                            )}
                            {renderedColumns.map((column) => renderCell(column, item))}
                            {!isSelectionMode && (
                                <TableCell align={"right"}>
                                    <Box onClick={(e) => e.stopPropagation()}>
                                        <DotLegalOverflowMenu
                                            items={[
                                                { menuItem: translateString("entityGraphOpen"), onClick: () => openEntityGraph(item) },
                                                { menuItem: translateString("remove"), onClick: () => openRemoveEntity(item) },
                                            ]}
                                        />
                                    </Box>
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                />

                {/* Mirrors the scenario table's Add scenario button, so each stacked paper carries its own way in.
                    Hidden while selecting, where the toolbar's bulk buttons own the interaction. */}
                {!isSelectionMode && (
                    <Box sx={styles.addButtonContainer}>
                        <DotLegalButton buttonType="primary" onClick={openAddEntities}>
                            {translateString("addEntities")}
                        </DotLegalButton>
                    </Box>
                )}
            </TablePagePaper>

            {bulkAssess.isBulkAssessDialogOpen && (
                <BulkAssessDialog
                    riskAreaId={riskAreaId}
                    entities={bulkAssess.selectedEntities}
                    onRemoveEntity={bulkAssess.removeSelectedEntity}
                    onSaved={onBulkAssessSaved}
                    onClose={bulkAssess.closeBulkAssessDialog}
                />
            )}

            {bulkMitigate.isBulkMitigateDialogOpen && (
                <BulkMitigateDialog
                    riskAreaId={riskAreaId}
                    entities={bulkMitigate.selectedEntities}
                    onRemoveEntity={bulkMitigate.removeSelectedEntity}
                    onSaved={onBulkMitigateSaved}
                    onClose={bulkMitigate.closeBulkMitigateDialog}
                />
            )}

            {entityGraphTarget && (
                <RiskEntityGraphDialog
                    rootType={entityGraphTarget.type}
                    rootId={entityGraphTarget.entityId}
                    riskAreaId={riskAreaId}
                    onClose={closeEntityGraph}
                />
            )}

            {isAddEntitiesOpen && <AddEntitiesDialog riskAreaId={riskAreaId} onSaved={() => refetch()} onClose={closeAddEntities} />}

            {entityToRemove && <RemoveEntityDialog riskAreaId={riskAreaId} entity={entityToRemove} onRemoved={() => refetch()} onClose={closeRemoveEntity} />}

            {controlsDialogItem && <LinkedControlsDialog linkedControlIds={controlsDialogItem.linkedControlIds} onClose={closeControlsDialog} />}
        </>
    );
}

export default EntityAssessmentsTable;
